// One row of the staff-only certificate-platform catalog (GET /admin/certificate-platforms) — the
// upstream CA/ACME endpoints the Manager syncs certificates from. Matches
// docs/api-types/admin-certificate-platforms.md (manager-backend). NOT the tenant-facing
// CertificatePlatform (src/types/models/CertificatePlatform.ts), which never exposes `metadata`.
//
// Secrets are write-only: `api_key`/`api_secret` are accepted in request bodies but never returned —
// responses carry only the non-secret metadata subset plus the `has_api_credentials` presence flag.

export const CERTIFICATE_PLATFORM_TYPES = ['opnsense-api-trust', 'opnsense-api-acme', 'zerossl'] as const;

/** Driver identifier. Immutable after creation — the backend rejects any PATCH that includes `type`. */
export type AdminCertificatePlatformType = (typeof CERTIFICATE_PLATFORM_TYPES)[number];

export const CERTIFICATE_PLATFORM_TYPE_LABELS: Record<AdminCertificatePlatformType, string> = {
  'opnsense-api-trust': 'OPNsense Trust (internal CA)',
  'opnsense-api-acme': 'OPNsense ACME (Let’s Encrypt)',
  zerossl: 'ZeroSSL (public CA)',
};

/** The ZeroSSL driver's default endpoint, used when a `zerossl` row stores no `base_url`. */
export const ZEROSSL_DEFAULT_BASE_URL = 'https://api.zerossl.com';

/** Friendly label for a platform type, falling back to the raw identifier for unknown values. */
export function certificatePlatformTypeLabel(type: string): string {
  return CERTIFICATE_PLATFORM_TYPE_LABELS[type as AdminCertificatePlatformType] ?? type;
}

/** Non-secret connection settings returned by every admin endpoint. Credentials never appear here. */
export interface AdminCertificatePlatformMetadata {
  /**
   * Endpoint root URL. `null` on a malformed hand-seeded row, and on `zerossl` rows that use the
   * driver default (ZEROSSL_DEFAULT_BASE_URL) — display the default, not a warning, for those.
   */
  base_url: string | null;
  /** Effective value: the stored flag, or the driver default `true` when the key is absent. */
  verify_tls: boolean;
  /**
   * Issuing-CA UUID on the upstream trust store. Only meaningful for `opnsense-api-trust`, where
   * it is required in practice: calls to the platform FAIL without it. `null` only on a legacy
   * hand-seeded row (backend validation predates the requirement) — such a row cannot sync.
   */
  ca_uuid: string | null;
}

export interface AdminCertificatePlatform {
  id: number;
  /** Display name, unique, ≤ 255 chars. */
  name: string;
  type: AdminCertificatePlatformType;
  /** `null` = shared (visible to every tenant); an int dedicates the platform to that client. */
  clientID: number | null;
  /** Resolved owning-client name; `null` for shared rows. */
  client_name: string | null;
  metadata: AdminCertificatePlatformMetadata;
  /** `false` = legacy row missing api_key/api_secret — it cannot sync until credentials are set. */
  has_api_credentials: boolean;
  support_revocation: boolean;
  support_private_key: boolean;
  support_ca_bundle: boolean;
  /** Synced `certificate` rows on this platform (attributed + orphaned; the split is not in the row). */
  certificate_count: number;
  /** ISO-8601 UTC timestamp of the last completed sync; `null` = never synced. */
  last_synced_at: string | null;
  /** Raw DB `YYYY-MM-DD HH:MM:SS` string (NOT NULL column). */
  created_at: string;
  /** Raw DB `YYYY-MM-DD HH:MM:SS` string (NOT NULL column). */
  updated_at: string;
}

/**
 * Metadata for POST /admin/certificate-platforms. Unknown keys are rejected — send exactly these.
 * The required set is TYPE-SPECIFIC: OPNsense types take base_url + api_key + api_secret;
 * `zerossl` takes api_key only (base_url optional, defaults to ZEROSSL_DEFAULT_BASE_URL) and the
 * backend REJECTS api_secret / ca_uuid for it as unknown keys — omit them, don't send them empty.
 */
export interface AdminCertificatePlatformCreateMetadata {
  /** Required for the OPNsense types; optional for `zerossl` (omit to use the driver default). */
  base_url?: string;
  api_key: string;
  /** OPNsense types only — rejected for `zerossl`. */
  api_secret?: string;
  /** Absent = driver default (`true`). Must be a real JSON boolean. */
  verify_tls?: boolean;
  /**
   * UUID; normalized to lowercase server-side. Only meaningful for `opnsense-api-trust`, where the
   * UI always requires it (backend validation allows it absent, but the Trust driver's calls fail
   * without it). Omit for ACME.
   */
  ca_uuid?: string;
}

export interface AdminCertificatePlatformCreateRequest {
  name: string;
  type: AdminCertificatePlatformType;
  /**
   * Positive int (must exist) dedicates the platform; `null`/absent = shared. REQUIRED (non-null)
   * for `zerossl` — always-dedicated: one customer's API key must never serve other tenants.
   */
  clientID?: number | null;
  metadata: AdminCertificatePlatformCreateMetadata;
}

/**
 * Metadata for PATCH — merged, not replaced: an absent key keeps its stored value (credentials
 * survive an edit when omitted), an explicit `null` on verify_tls/ca_uuid removes the stored key.
 * Changing `base_url` requires re-sending the TYPE's full credential set in the same request
 * (OPNsense: `api_key` AND `api_secret`; `zerossl`: `api_key` alone — its `api_secret`/`ca_uuid`
 * are rejected as unknown keys). Anti-exfiltration — stored credentials are never sent to a new
 * endpoint.
 */
export interface AdminCertificatePlatformUpdateMetadata {
  base_url?: string;
  /** Never null/empty — include only when rotating to a new value. */
  api_key?: string;
  /** Never null/empty — include only when rotating to a new value. */
  api_secret?: string;
  /** `null` removes the stored key (reverts to the driver default `true`). */
  verify_tls?: boolean | null;
  /**
   * `null` clears the stored UUID (accepted by the contract, but the UI never sends it — a Trust
   * platform's calls fail without a CA UUID, so clearing is blocked client-side).
   */
  ca_uuid?: string | null;
}

/** Partial update — send only what changed; an empty change-set is rejected. Never send `type`. */
export interface AdminCertificatePlatformUpdateRequest {
  name?: string;
  /**
   * An int dedicates the platform (blocked while it holds other tenants' certificates); explicit
   * `null` makes it shared — REJECTED on `zerossl` rows (always-dedicated).
   */
  clientID?: number | null;
  metadata?: AdminCertificatePlatformUpdateMetadata;
}
